import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { addActiveGame, updateActiveGame, removeActiveGame, setMyCards, setHandInfo, addChatMessage, fetchMyHand, fetchGameSnapshot, kickedFromGame, sngFinished, setBuyInCancelled, tournamentStarted, tournamentMoved, tournamentFinished, goToGameList } from './gameSlice';
import { blastReceived, fetchNotifications } from './notificationsSlice';
import { getPlayerChips } from './playerInfoSlice';
import {
  privateStateReceived, snapshotReceived, roomCleared, allRoomsCleared,
  pupPlayCleared, pupPrivateReveal, pupOwnCardsPeeked, pupCardModified, setMyPupCards,
  wildCardsReceived,
  setMyStagedPupCards, pupNoticeAdded, pupRoundDisplaysCleared,
  pupSeatRevealsCleared,
} from './pupSlice';
import { translatePupRoomEvent } from './pupProtocolAdapter';
import { decodeRoomEvent, isOlderRoomEvent } from './roomProtocolAdapter';
import { mutatedHoleSeatForResolvedPlay } from './pupRevealPolicy';
import { decodeGameEvent } from './eventLogProtocolAdapter';
import { actionLogEventsReceived, actionLogCleared, actionLogAllCleared, fetchHandHistory } from './actionLogSlice';
import { fetchPupCatalog, selectPupCatalog } from './pupCatalogSlice';
import { playFoldSwoosh, playTurnStart, playChipsSmall, playChipsMed, playCardSnap, playFreeze } from '../utilities/sounds';
import { isTokenValid } from '../utilities/auth';
import { BASE_URL } from '../utilities/BASE_URL';
import { fetchWithToken } from '../utilities/fetchWithToken';

// How long after a DEFERRED leave to re-read the wallet. The server holds the winner display for
// ~5s and settles the queued seat (cashing a cash stack out) right before the next deal, so this
// must clear that hold with margin.
const DEFERRED_SETTLE_REFETCH_MS = 8000;

// True if any seat in the new players map went from not-folded to folded.
const aPlayerJustFolded = (prevPlayers = {}, nextPlayers = {}) =>
  Object.entries(nextPlayers).some(
    ([seat, p]) => p?.isFolded && !prevPlayers[seat]?.isFolded
  );

// Largest single-seat currentBet increase between snapshots (0 if none bet).
const biggestBetIncrease = (prevPlayers = {}, nextPlayers = {}) =>
  Object.entries(nextPlayers).reduce((max, [seat, p]) => {
    const inc = (p?.currentBet || 0) - (prevPlayers[seat]?.currentBet || 0);
    return inc > max ? inc : max;
  }, 0);

// Single unified message handler for a room — used for both spectators and seated players
const buildRoomHandler = (store, roomId) => {
  // STOMP reconnects can redeliver an application frame around a transport failure. Server
  // eventIds make those deliveries idempotent without treating a later reconnect snapshot's
  // eventVersion as proof that delayed presentation frames should be discarded.
  const seenEventIds = new Set();
  return message => {
    const decoded = decodeRoomEvent(message.body, roomId);
    if (decoded.error) {
      console.warn(`[WS] dropped ${decoded.error} room frame for ${roomId}`);
      return;
    }
    const data = decoded.event;
    const protocol = decoded.protocol;
    if (isOlderRoomEvent(store.getState().games.activeGames?.[roomId], protocol)) return;
    const eventId = protocol.eventId;
    if (eventId && seenEventIds.has(eventId)) return;
    if (eventId) {
      seenEventIds.add(eventId);
      if (seenEventIds.size > 512) {
        seenEventIds.delete(seenEventIds.values().next().value);
      }
    }

    if (Object.keys(protocol).length && store.getState().games.activeGames?.[roomId]) {
      store.dispatch(updateActiveGame({ id: roomId, changes: protocol }));
    }
    // Wildness rides every authoritative game payload as POSITIONS, and is applied with
    // replace-semantics (see pupSlice.wildRefs). Doing it here — rather than off the
    // PUP_CARDS_REVEALED flash — is what makes wild badges survive a reconnect.
    if (Array.isArray(data.payload?.wildCards)) {
      store.dispatch(wildCardsReceived({ roomId, wildCards: data.payload.wildCards }));
    }
    console.log(`[WS] room ${roomId}:`, data.type);

    switch (data.type) {
      case 'PLAYER_JOINED':
      case 'PLAYER_LEFT': {
        // Backend sends the full players map keyed by seatNumber
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            players: data.payload.players,
            numPlayers: data.payload.numPlayers
          }
        }));
        // Reconcile our OWN seat against the authoritative map: a deferred leave settles
        // as a plain PLAYER_LEFT (no PLAYER_KICKED), so if we re-opened this table while
        // the old seat still existed, mySeats can go stale here. When the map no longer
        // shows us at the seat we think we hold, re-verify via /games/mycards (whose
        // fulfilled handler clears a stale seat).
        const state = store.getState();
        const mySeat = state.games.mySeats[roomId];
        if (mySeat != null) {
          const me = state.playerInfo.username;
          const occupant = data.payload.players?.[mySeat];
          if (!occupant || (me && occupant.username !== me)) {
            store.dispatch(fetchMyHand({ roomId: Number(roomId) }));
          }
        }
        break;
      }

      case 'PLAYER_SITTING': {
        // Merge pending player into existing players map
        const { seatNumber, username } = data.payload;
        const currentPlayers = { ...(store.getState().games.activeGames[roomId]?.players || {}) };
        currentPlayers[seatNumber] = { username, isPending: true };
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: { players: currentPlayers }
        }));
        break;
      }

      case 'PLAYER_CANCEL_SIT': {
        // Remove the pending seat entry
        const { seatNumber } = data.payload;
        const currentPlayers = { ...(store.getState().games.activeGames[roomId]?.players || {}) };
        delete currentPlayers[seatNumber];
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: { players: currentPlayers }
        }));
        break;
      }

      case 'GAME_DEAL':
      case 'GAME_ACTION': {
        const prevGame = store.getState().games.activeGames[roomId];
        if (data.type === 'GAME_ACTION' && data.payload.players) {
          if (aPlayerJustFolded(prevGame?.players, data.payload.players)) {
            playFoldSwoosh();
          }
          // Chip sound when someone puts chips in (silent for blinds on GAME_DEAL)
          const betInc = biggestBetIncrease(prevGame?.players, data.payload.players);
          if (betInc > 0) {
            (betInc >= (prevGame?.bigBlind || 1) * 10 ? playChipsMed : playChipsSmall)();
          }
        }
        // "It's your turn" alert: fires only when the active seat becomes this
        // user's seat (and wasn't already), so it doesn't repeat on resends.
        const mySeat = store.getState().games.mySeats?.[roomId];
        const nextActive = data.payload.activePlayerPosition;
        if (mySeat != null && nextActive === mySeat && prevGame?.activePlayerPosition !== mySeat) {
          playTurnStart();
        }
        // lastEventType/eventSeq let the chip-animation hook classify each server
        // event precisely (GAME_DEAL blinds vs GAME_ACTION round transitions).
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            ...data.payload,
            lastEventType: data.type,
            eventSeq: (prevGame?.eventSeq || 0) + 1,
            // A new deal clears any lingering opening dealer-button draw display and the
            // previous round's per-seat showdown reveals (incl. a negotiated-split banner /
            // Binding Contract nets / Run It Twice's second board — GAME_SHOWDOWN merges
            // them in, so they must sweep here).
            ...(data.type === 'GAME_DEAL'
              ? { dealerDraw: null, showdownReveals: null, wonByFold: false,
                  negotiated: false, contractResults: null, secondBoard: null }
              : {})
          }
        }));
        if (data.type === 'GAME_DEAL') {
          // A new deal also sweeps any on-table Power-Up display from the previous round,
          // plus the round-scoped reveal displays (wild badges, forced reveals, peeks).
          store.dispatch(pupPlayCleared({ roomId }));
          store.dispatch(pupRoundDisplaysCleared({ roomId }));
        }
        if (['waiting', 'start', 'exchange', 'predeal'].includes(data.payload.currentStage)) {
          // No round in progress OR a pre-deal placeholder stage (Exchange / Pre-Deal) —
          // no hole cards are dealt yet, so clear any lingering hand from the last round.
          store.dispatch(setMyCards({ roomId, cards: [] }));
        } else if (data.type === 'GAME_DEAL') {
          // New round dealt — deterministically pull this user's own hole cards
          store.dispatch(fetchMyHand({ roomId }));
        }
        break;
      }

      // ---- Power-Up plays (card on the table) ------------------------------------------
      case 'PUP_PLAY_PENDING': {
        // Someone played a Power-Up: show it on the felt with its window countdown —
        // shimmering through the reveal beat (mode 'reveal', effect fires when it ends),
        // awaiting a target pick (mode 'targeting'; if it's OUR play, GameTable also
        // makes opponent seats clickable), or awaiting a hole-card discard pick (mode
        // 'discarding'; the hero's own hole cards become the picker). Snap only when the
        // card genuinely LANDS: a discard pick RE-sends PENDING (mode flips to 'reveal')
        // for a card already sitting on the felt, and a re-surfaced base after an interrupt
        // popped RE-sends its PENDING too — snap only for a genuinely new playId (a fresh play
        // or a landed Interrupt stacking on top).
        const existingPlays = store.getState().pup.plays?.[roomId] || [];
        const isNewPlay = data.payload.playId == null
          || !existingPlays.some((p) => p.playId === data.payload.playId);
        if (isNewPlay) playCardSnap();
        translatePupRoomEvent(roomId, data).forEach(store.dispatch);
        break;
      }

      case 'PUP_TARGET_CHOSEN': {
        // Target picked: draw the card→target line for the (2s) line beat.
        translatePupRoomEvent(roomId, data).forEach(store.dispatch);
        break;
      }

      case 'PUP_PLAY_RESOLVED': {
        // Effect applied (the accompanying GAME_ACTION snapshot carries the new state,
        // including the restarted phase clock + any noBetRaise flag) — clear the display.
        // If the resolved play MUTATED a seat's hole cards, that seat's pinned mid-round
        // reveals (Go Fish / Peek) are no longer trustworthy — drop them so the seat falls
        // back to face-down backs instead of asserting a card that left or changed:
        //   - a discard card (Sacrifice/Exchange) mutated the PLAYER's own hand;
        //   - a HOLE-zone card-target (±One / Suit Change) mutated targetSeat's card;
        //   - One Man's Trash rewrites the player's whole hand (untargeted — matched by
        //     code; a future untargeted hand-rewriting card needs the same treatment).
        const resolvedEntry = selectPupCatalog(store.getState())?.[data.payload.card];
        const mutatedSeat = mutatedHoleSeatForResolvedPlay(data.payload, resolvedEntry);
        if (mutatedSeat != null) {
          store.dispatch(pupSeatRevealsCleared({ roomId, seat: mutatedSeat }));
        }
        // A failed Negotiation vote resolves quietly (the round just resumes) — surface the
        // outcome as a toast so the table knows the split was rejected. A PASSED vote needs
        // nothing: the GAME_SHOWDOWN that follows carries the whole story.
        if (data.payload.votePassed === false) {
          const yes = (data.payload.yesSeats || []).length;
          const no = (data.payload.noSeats || []).length;
          store.dispatch(pupNoticeAdded({
            roomId, kind: 'vote',
            text: `Negotiation failed — ${yes} yes / ${no} no. The round plays on.`,
          }));
        }
        // Remove just THIS play from the stack (by playId) — any interrupt/base still pending
        // stays on the felt.
        translatePupRoomEvent(roomId, data).forEach(store.dispatch);
        break;
      }

      case 'PUP_PLAY_CANCELLED': {
        // Targeting window expired (card returned to its owner, locked for the round) — or,
        // with countered: true, a Cancel KILLED this play (card discarded, never returned).
        // Either way the owner's client re-pulls its private state: the same REST fetch
        // heals both shapes (returned+locked vs gone).
        translatePupRoomEvent(roomId, data).forEach(store.dispatch);
        if (data.payload.countered) {
          const counteredEntry = selectPupCatalog(store.getState())?.[data.payload.card];
          store.dispatch(pupNoticeAdded({
            roomId, kind: 'counter',
            text: `${counteredEntry?.name || data.payload.card} was countered!`,
          }));
        }
        const mySeatNow = store.getState().games.mySeats?.[roomId];
        if (mySeatNow != null && mySeatNow === data.payload.seat) {
          store.dispatch(fetchMyHand({ roomId: Number(roomId) }));
        }
        break;
      }

      case 'PUP_CARDS_REVEALED': {
        // A resolved Power-Up force-revealed cards to the whole table: per-seat hole cards
        // (Go Fish's named rank) or the wild token(s) a wild effect landed on (seat -1).
        // Displays persist until the next deal (pupRoundDisplaysCleared above).
        // kind "guess" is a Stream Sniper shot: nothing is revealed (reveals is empty) —
        // announce the hit/miss as a toast instead.
        if (data.payload.kind === 'guess') {
          const gameNow = store.getState().games.activeGames?.[roomId];
          const sniperName = gameNow?.players?.[data.payload.seat]?.username || `Seat ${data.payload.seat}`;
          const targetName = gameNow?.players?.[data.payload.targetSeat]?.username || `Seat ${data.payload.targetSeat}`;
          const hit = data.payload.correct;
          store.dispatch(pupNoticeAdded({
            roomId, kind: 'guess',
            text: hit
              ? `🎯 ${sniperName} guessed ${targetName}'s cards (${data.payload.detail}) — hit!`
                + (data.payload.amount > 0 ? ` Collects ${data.payload.amount}.` : '')
                + (data.payload.destroyed ? ' The Stream Sniper is spent.' : '')
              : `🎯 ${sniperName} guessed ${targetName}'s cards (${data.payload.detail}) — miss.`,
          }));
          break;
        }
        translatePupRoomEvent(roomId, data).forEach(store.dispatch);
        break;
      }

      case 'PUP_CARD_MODIFIED': {
        // A resolved Power-Up rewrote a COMMUNITY board card in place (Substitution, or a
        // rank/suit shift targeting the board). Public — badge the changed board card with a
        // "modified" indicator (who changed it + hold-to-see-original) until the next deal.
        // Hole-card mutations are private and arrive via /user/queue/private/pupCardModified.
        translatePupRoomEvent(roomId, data).forEach(store.dispatch);
        break;
      }

      case 'PUP_AUTO_PLAYED': {
        // An AUTOMATIC one-shot fired at the deal (a bomb pot, the Bounty button): no
        // pending play / felt window exists — the accompanying GAME_DEAL payload already
        // carries the resulting bombPot/bountyButton state — so just toast who drew what.
        const autoEntry = selectPupCatalog(store.getState())?.[data.payload.card];
        const autoName = autoEntry?.name || data.payload.card;
        const drawer = data.payload.username || `Seat ${data.payload.seat}`;
        store.dispatch(pupNoticeAdded({
          roomId, kind: 'auto',
          text: `💥 ${drawer}'s ${autoName} fires automatically!`,
        }));
        break;
      }

      // ---- Freeze time (player-controlled table freeze) --------------------------------
      case 'FREEZE_STARTED': {
        // A player froze time (active Freeze press, or a queued freeze firing at its
        // stage/phase point) — room-wide sound, then merge the freeze fields directly.
        // Belt only: the paired authoritative GAME_ACTION snapshot carries the same fields
        // (this event's stateChanged=false stamps pass the stateVersion merge guard).
        playFreeze();
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            frozenSeats: data.payload.seats || [],
            freezeKind: data.payload.kind || null,
            freezeStartTime: data.payload.startTime ?? null,
            freezeDurationMs: data.payload.durationMs ?? null,
          }
        }));
        break;
      }

      case 'FREEZE_ENDED': {
        // The freeze ended (unfreeze / accepted action / expiry / freezer left) — clear the
        // freeze fields. Same belt: the following GAME_ACTION snapshot is authoritative.
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            frozenSeats: [], freezeKind: null, freezeStartTime: null, freezeDurationMs: null,
          }
        }));
        break;
      }

      case 'SHOWDOWN_BEGIN': {
        // Enter the sequenced reveal: cards go face-down, the pot stays put, and winners/pots
        // are cleared so the chip-fly + result banner stay suppressed until reveals finish.
        // Reveals arrive one-by-one via SHOWDOWN_REVEAL. No eventSeq bump → the chip
        // animation hook treats this as idle (keeps the pot displayed).
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            ...data.payload,
            currentStage: 'showdown',
            showdownReveals: {},
            winners: [],
            pots: [],
            wonByFold: false
          }
        }));
        break;
      }

      case 'SHOWDOWN_REVEAL': {
        // One player shows (cards flip face-up) or mucks (cards vanish, like a fold). Merge
        // into the per-seat reveal map; GameTable renders from it. handName (on a show) lets
        // the winner's bubble display their made hand.
        // bestHandRefs names the winning five by POSITION ([{zone, seat, index}]) — what the
        // highlight uses. Matching by token lights the wrong card when two cards share one,
        // which the rank/suit-editing Power-Ups make possible on purpose.
        const { seat, action, holeCards, handName, bestHand, bestHandRefs } = data.payload;
        const prevReveals = store.getState().games.activeGames[roomId]?.showdownReveals || {};
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: { showdownReveals: { ...prevReveals, [seat]: { action, holeCards, handName, bestHand, bestHandRefs } } }
        }));
        if (action === 'muck') playFoldSwoosh(); else playCardSnap();
        break;
      }

      case 'GAME_SHOWDOWN': {
        const prevGame = store.getState().games.activeGames[roomId];
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            ...data.payload,
            currentStage: 'showdown',
            lastEventType: data.type,
            eventSeq: (prevGame?.eventSeq || 0) + 1
          }
        }));
        break;
      }

      case 'DEALER_DRAW': {
        // Opening high-card-for-the-button draw: one face-up card per active seat.
        // The DealerDraw component reveals them 0.5s apart in seat order.
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: { dealerDraw: { cards: data.payload.cards, winnerSeat: null } }
        }));
        break;
      }

      case 'DEALER_DRAW_DONE': {
        // Winner takes the button; flag the seat so its card highlights before the
        // cards clear (cleared on the GAME_DEAL that starts the first round).
        const prevGame = store.getState().games.activeGames[roomId];
        store.dispatch(updateActiveGame({
          id: roomId,
          changes: {
            dealerDraw: { ...(prevGame?.dealerDraw || {}), winnerSeat: data.payload.winnerSeat },
            dealerSeat: data.payload.dealerSeat
          }
        }));
        break;
      }

      case 'CHAT_MESSAGE':
        store.dispatch(addChatMessage({ roomId, message: data.payload }));
        break;

      default:
        console.log('[WS] Unknown message type:', data.type);
    }
  };
};

// Handler for a room's PUBLIC event-log topic (/topic/game/{id}/events) — the ActionLog feed.
// The event shape is the same one GET /handhistory returns, so live + historical share a formatter.
const buildEventLogHandler = (store, roomId) => (message) => {
  const decoded = decodeGameEvent(message.body, roomId);
  if (decoded.error) {
    console.warn(`[WS] dropped game event for room ${roomId}: ${decoded.error}`);
    return;
  }
  store.dispatch(actionLogEventsReceived({ roomId, events: [decoded.event] }));
};

export const socketMiddleware = () => {
  let client = null;
  // Every explicit credential replacement advances this generation. Async deactivate promises
  // from an older login are allowed to finish, but can no longer reconnect their obsolete JWT.
  let socketGeneration = 0;

  // roomHandlers: persists across reconnects — stores the message handler per roomId
  let roomHandlers = {};
  // eventHandlers: the parallel handler for each room's public event-log topic (ActionLog feed)
  let eventHandlers = {};
  // stompSubs: STOMP subscription objects — recreated on every connect/reconnect
  let stompSubs = {};
  // eventSubs: the parallel /topic/game/{id}/events subscription objects
  let eventSubs = {};

  // Subscribe a room's state topic + (when registered) its event-log topic, replacing any existing.
  const subscribeStompRoom = (roomId) => {
    if (stompSubs[roomId]) { try { stompSubs[roomId].unsubscribe(); } catch (e) {} }
    const owningClient = client;
    const roomHandler = roomHandlers[roomId];
    stompSubs[roomId] = owningClient.subscribe(`/topic/game/${roomId}`, (message) => {
      if (client === owningClient) roomHandler(message);
    });
    if (eventHandlers[roomId]) {
      if (eventSubs[roomId]) { try { eventSubs[roomId].unsubscribe(); } catch (e) {} }
      const eventHandler = eventHandlers[roomId];
      eventSubs[roomId] = owningClient.subscribe(`/topic/game/${roomId}/events`, (message) => {
        if (client === owningClient) eventHandler(message);
      });
    }
    console.log(`[WS] Subscribed to room ${roomId}`);
  };

  // Re-subscribe to all tracked rooms. Called both on initial connect and on reconnect.
  const resubscribeAll = () => {
    Object.keys(roomHandlers).forEach((roomId) => subscribeStompRoom(roomId));
  };

  // Register or update a room handler (+ its event-log handler) and subscribe if already connected
  const subscribeRoom = (roomId, handler, eventHandler) => {
    roomHandlers[roomId] = handler;
    if (eventHandler) eventHandlers[roomId] = eventHandler;
    if (client && client.connected) {
      subscribeStompRoom(roomId);
    }
    // If not connected yet, resubscribeAll() in onConnect will pick this up
  };

  const unsubscribeRoom = (roomId) => {
    if (stompSubs[roomId]) {
      try { stompSubs[roomId].unsubscribe(); } catch (e) {}
      delete stompSubs[roomId];
    }
    if (eventSubs[roomId]) {
      try { eventSubs[roomId].unsubscribe(); } catch (e) {}
      delete eventSubs[roomId];
    }
    delete roomHandlers[roomId];
    delete eventHandlers[roomId];
    console.log(`[WS] Unsubscribed from room ${roomId}`);
  };

  // Open the STOMP socket for a valid token. Called both after an explicit login AND on app
  // boot when a token is already in localStorage (page refresh / deep-link) — without the
  // boot path, an authenticated reload leaves a dead socket and a frozen table until the next
  // login. Guarded so a login that follows a boot-time connect doesn't open a second client.
  const connectSocket = (store, token) => {
    if (!isTokenValid(token)) return;
    if (client && client.active) return; // already connecting/connected
    const socketClient = new Client({
      // STOMP calls the factory again for each reconnect. A SockJS instance cannot be
      // reopened after it closes, so always create a fresh transport here.
      webSocketFactory: () => new SockJS(`${BASE_URL}/ws`),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectHeaders: { Authorization: `Bearer ${token}` }
    });
    client = socketClient;

    socketClient.onConnect = () => {
      // A superseded transport can finish CONNECT while its deactivate is in flight. It must
      // never install user queues after a newer login/logout has taken ownership.
      if (client !== socketClient) return;
      console.log('[WS] Connected');
      const currentOnly = (handler) => (message) => {
        if (client === socketClient) handler(message);
      };
      // System-level user queue
      socketClient.subscribe('/user/queue/updates', currentOnly(message => {
        const data = JSON.parse(message.body);
        switch (data.type) {
          case 'GAME_CREATED': store.dispatch(addActiveGame(data.payload)); break;
          case 'GAME_UPDATED': store.dispatch(updateActiveGame({ id: data.payload.id, changes: data.payload })); break;
          case 'GAME_DELETED': store.dispatch(removeActiveGame(data.payload.id)); break;
          // Server auto-removed us from a table; leave it client-side (unsubscribe
          // handled below by the 'game/kickedFromGame' middleware case).
          case 'PLAYER_KICKED': store.dispatch(kickedFromGame(data.payload.roomId)); break;
          // Our Sit-and-Go finish (bust / leave / win): the seat — or the whole finished
          // game — is already gone server-side and any payout is settled; tear the table
          // down and show the result popup ('game/sngFinished' below unsubscribes).
          case 'SNG_RESULT': store.dispatch(sngFinished(data.payload)); break;
          // Multi-table Tournament: seated at launch/late-entry, moved by the balancer, or our
          // finish/cancellation. The 'game/tournament*' middleware cases below (un)subscribe rooms.
          case 'TOURNAMENT_STARTED': store.dispatch(tournamentStarted(data.payload)); break;
          case 'TOURNAMENT_MOVE': store.dispatch(tournamentMoved(data.payload)); break;
          case 'TOURNAMENT_RESULT': store.dispatch(tournamentFinished(data.payload)); break;
          // Server released our pending seat for sitting on the buy-in prompt too long.
          case 'BUYIN_CANCELLED': store.dispatch(setBuyInCancelled(data.payload)); break;
          default: break;
        }
      }));
      // Admin notification blasts — one broadcast reaches every connected client so the
      // home-screen bell badge lights up live (persistence + read state are REST-side).
      socketClient.subscribe('/topic/announcements', currentOnly(message => {
        const data = JSON.parse(message.body);
        if (data.type === 'ADMIN_BLAST') store.dispatch(blastReceived(data.payload));
      }));
      socketClient.subscribe('/user/queue/private/cards', currentOnly(message => {
        const { roomId, holeCards, holeCardIds } = JSON.parse(message.body);
        store.dispatch(setMyCards({ roomId, cards: holeCards ? holeCards.split(' ') : [] }));
        // The identities of those same cards, positionally aligned — how this client looks a
        // card's provenance up by the CARD rather than by the slot it currently sits in.
        store.dispatch(privateStateReceived({ roomId, holeCardIds: holeCardIds || '' }));
      }));
      // This player's current best-hand hint (e.g. "Pair of 7's"), re-pushed each stage.
      socketClient.subscribe('/user/queue/private/handInfo', currentOnly(message => {
        const { roomId, handInfo } = JSON.parse(message.body);
        store.dispatch(setHandInfo({ roomId, handInfo: handInfo || '' }));
      }));
      // A Power-Up revealed cards privately to THIS player (Peek/Peeper: one of an
      // opponent's hole cards, pinned face-up at their seat until the next deal).
      socketClient.subscribe('/user/queue/private/pupReveal', currentOnly(message => {
        store.dispatch(pupPrivateReveal(JSON.parse(message.body)));
      }));
      // The flip side: a Peek/Peeper secretly showed an opponent one of THIS player's OWN
      // hole cards. Badge that card with an owner-only eye (+ who saw it) until the next deal.
      socketClient.subscribe('/user/queue/private/pupPeeked', currentOnly(message => {
        store.dispatch(pupOwnCardsPeeked(JSON.parse(message.body)));
      }));
      // A value-modifying Power-Up (Plus/Minus One, Suit Change, Set Ace, One Man's Trash,
      // Magic Trick) rewrote one of THIS player's OWN hole cards. Badge the changed card with
      // an owner-only "modified" indicator (who changed it + hold-to-see-original) until the
      // next deal. Community-card mutations arrive publicly via PUP_CARD_MODIFIED on the room.
      socketClient.subscribe('/user/queue/private/pupCardModified', currentOnly(message => {
        store.dispatch(pupCardModified(JSON.parse(message.body)));
      }));
      // This player's whole pup hand, re-pushed after a mid-round change (a Draw Pup /
      // Demonic Ritual / Sacrifice grew it outside the deal grant). Also carries the AUTO-PLAY
      // box: a card the server auto-played leaves the box with no HTTP response to shrink it.
      socketClient.subscribe('/user/queue/private/pupHand', currentOnly(message => {
        const { roomId, pupCards, pupCardInstances, queuedPupCards } = JSON.parse(message.body);
        store.dispatch(setMyPupCards({
          roomId, pupCards: pupCards || [], pupCardInstances, queuedPupCards,
        }));
      }));
      // This player's exchange-box (staged) pile, re-pushed after the Exchange stage swapped 3
      // staged silvers for a gold (the box shrank). Persists across deals — never swept on a deal.
      socketClient.subscribe('/user/queue/private/pupStaged', currentOnly(message => {
        const { roomId, stagedPupCards, stagedCommitted } = JSON.parse(message.body);
        store.dispatch(setMyStagedPupCards({
          roomId, stagedPupCards: stagedPupCards || [], stagedCommitted: stagedCommitted || [],
        }));
      }));
      // This player's PRIVATE game-log events (visibility private:<seat>) for the ActionLog. The
      // envelope carries its own roomId; only feed a room we are actually tracking. The server never
      // sends another seat's private events — this is the client-side belt to that (never fabricated).
      socketClient.subscribe('/user/queue/game-events', currentOnly(message => {
        const decoded = decodeGameEvent(message.body, null);
        if (decoded.error || !roomHandlers[decoded.roomId]) return;
        store.dispatch(actionLogEventsReceived({ roomId: decoded.roomId, events: [decoded.event] }));
      }));
      // Re-subscribe to all game rooms (handles reconnects and initial race condition)
      resubscribeAll();

      // Reconcile every tracked room from one authoritative locked snapshot. This heals
      // missed table events, private cards, staged cards, and in-flight Power-Up windows;
      // it also tears down a stale seated table if inactivity removal happened offline.
      const { activeGames } = store.getState().games;
      Object.keys(activeGames).forEach(roomId => {
        store.dispatch(fetchGameSnapshot({ roomId: Number(roomId) }));
      });

      // Same self-heal for the notification bell: a (re)connect may have missed
      // ADMIN_BLAST pushes, so replace items + unread count from the authoritative list.
      store.dispatch(fetchNotifications());

      // Seat self-heal: a player seated while offline (a tournament launch, a balancer move,
      // or their own live-tournament "leave" — which only parks them AWAY, seat and chips
      // intact) has no client record of the table. Re-open every tournament/Sit-and-Go seat we
      // aren't already tracking so the "Rejoin Table" box is reachable. Covers boot, login and
      // every reconnect, since onConnect fires for all three.
      // /games/myseats rather than /tournaments/active: the latter filters on a tournament row,
      // which a Sit-and-Go does not have at all.
      // Cash seats are deliberately NOT auto-reopened — a cash player who closed a table
      // meant it, and their stack is safe in the one-hour return escrow.
      fetchWithToken(`${BASE_URL}/games/myseats`)
        .then(res => (res.ok ? res.json() : { seats: [] }))
        .then(data => {
          (data.seats || [])
            .filter(t => t.isSitAndGo || t.isTournament)
            .forEach(t => {
              if (!roomHandlers[t.roomId]) store.dispatch(tournamentStarted(t));
            });
        })
        .catch(() => {});
    };

    socketClient.onStompError = frame => {
      console.error('[WS] STOMP error:', frame.headers.message);
    };

    socketClient.activate();
  };

  const resetStompSubscriptions = () => {
    stompSubs = {};
    eventSubs = {};
  };

  const replaceSocket = (store, token) => {
    const generation = ++socketGeneration;
    const previous = client;
    client = null;
    resetStompSubscriptions();

    const reconnectLatest = () => {
      if (generation === socketGeneration) connectSocket(store, token);
    };
    if (!previous) {
      reconnectLatest();
      return;
    }
    try {
      Promise.resolve(previous.deactivate())
        .catch((error) => console.warn('[WS] Failed to deactivate replaced socket:', error))
        .finally(reconnectLatest);
    } catch (error) {
      console.warn('[WS] Failed to deactivate replaced socket:', error);
      reconnectLatest();
    }
  };

  const stopSocket = () => {
    socketGeneration += 1; // cancels any reconnectLatest waiting on an older deactivate
    const previous = client;
    client = null;
    resetStompSubscriptions();
    if (previous) {
      try { previous.deactivate(); } catch (error) {
        console.warn('[WS] Failed to deactivate socket:', error);
      }
    }
  };

  return store => next => action => {
    switch (action.type) {

      // App boot with a persisted token (refresh / deep-link): bring the socket up so live
      // game state flows without forcing a re-login. No-op if the token is missing/expired.
      case 'auth/connectSocket':
        connectSocket(store, store.getState().auth.token);
        break;

      case 'auth/loginUser/fulfilled':
      case 'auth/googleLoginUser/fulfilled': {
        // Explicit login is an account boundary. Close every old table/private slice before
        // rebinding STOMP so user A's cards cannot remain visible to user B in the same tab.
        store.dispatch(goToGameList());
        // Google sign-in yields the same JWT as a form login, so both replace the socket here.
        // Use the token from the action payload, not getState(): middleware runs before the
        // reducer, so auth.token isn't updated yet at this point.
        replaceSocket(store, action.payload);
        // Re-attempt the Power-Up catalog fetch too: the boot-time fetch fails silently if
        // the backend was down at page load, and a login is the natural retry moment. The
        // thunk's own condition dedupes this against a boot fetch that already succeeded.
        store.dispatch(fetchPupCatalog());
        break;
      }

      case 'auth/setToken': {
        // Email changes issue a JWT with a new subject. The STOMP principal is fixed at
        // CONNECT, so replace the connection and re-subscribe with the new identity.
        replaceSocket(store, action.payload);
        break;
      }

      case 'game/openGame/fulfilled':
      case 'game/openSecondGame/fulfilled':
      case 'game/quickSeat/fulfilled': {
        const roomId = action.payload.id;
        // Only register if not already tracking this room (don't downgrade a player sub)
        if (!roomHandlers[roomId]) {
          subscribeRoom(roomId, buildRoomHandler(store, roomId), buildEventLogHandler(store, roomId));
        }
        break;
      }

      case 'game/joinGame/fulfilled': {
        const { roomId } = action.payload;
        // Refresh subscription (handler is the same, but ensures we're subscribed)
        subscribeRoom(roomId, buildRoomHandler(store, roomId), buildEventLogHandler(store, roomId));
        // A returning seat can have a saved Pup hand immediately, even when joining
        // mid-round, so restore private state instead of waiting for the next deal.
        store.dispatch(fetchMyHand({ roomId }));
        break;
      }

      case 'game/standUp/fulfilled':
      case 'game/leaveTable/fulfilled': {
        if (action.type === 'game/standUp/fulfilled') {
          store.dispatch(roomCleared({ roomId: action.payload.roomId }));
        }
        // A DEFERRED leave answers immediately but the seat is settled — and the cash stack paid
        // back to the wallet — only after the result hold finishes, several seconds later. The
        // response's balance predates that, and TopMenu's own mount refetch fires right now, so
        // without this the top bar would sit on the pre-cash-out number until the next remount.
        // Re-read the authoritative balance once the settle has certainly landed.
        if (action.payload?.deferred === true) {
          setTimeout(() => store.dispatch(getPlayerChips()), DEFERRED_SETTLE_REFETCH_MS);
        }
        break;
      }

      case 'game/leaveTable/pending': {
        // Unsubscribe at press time, in step with the gameSlice teardown on pending — the
        // server response can lag seconds behind (leaves are deferred during a showdown /
        // determine-winner hold) and this client no longer renders the table anyway.
        unsubscribeRoom(action.meta.arg.roomId);
        store.dispatch(roomCleared({ roomId: action.meta.arg.roomId }));
        store.dispatch(actionLogCleared({ roomId: action.meta.arg.roomId }));
        break;
      }

      case 'game/removeGame':
      case 'game/removeActiveGame': {
        unsubscribeRoom(action.payload);
        store.dispatch(roomCleared({ roomId: action.payload }));
        store.dispatch(actionLogCleared({ roomId: action.payload }));
        break;
      }

      case 'game/kickedFromGame': {
        unsubscribeRoom(action.payload);
        store.dispatch(roomCleared({ roomId: action.payload }));
        store.dispatch(actionLogCleared({ roomId: action.payload }));
        break;
      }

      case 'game/sngFinished': {
        unsubscribeRoom(action.payload.roomId);
        store.dispatch(roomCleared({ roomId: action.payload.roomId }));
        store.dispatch(actionLogCleared({ roomId: action.payload.roomId }));
        break;
      }

      case 'game/tournamentStarted': {
        // Seated at a tournament table (launch or late entry). Subscribe + hydrate from a snapshot.
        const roomId = action.payload.roomId;
        subscribeRoom(roomId, buildRoomHandler(store, roomId), buildEventLogHandler(store, roomId));
        store.dispatch(fetchGameSnapshot({ roomId: Number(roomId) }));
        break;
      }

      case 'game/tournamentMoved': {
        // The balancer moved us: drop the old room's subscription, subscribe the new one, hydrate.
        const { fromRoomId, toRoomId } = action.payload;
        unsubscribeRoom(fromRoomId);
        store.dispatch(roomCleared({ roomId: fromRoomId }));
        store.dispatch(actionLogCleared({ roomId: fromRoomId }));
        subscribeRoom(toRoomId, buildRoomHandler(store, toRoomId), buildEventLogHandler(store, toRoomId));
        store.dispatch(fetchGameSnapshot({ roomId: Number(toRoomId) }));
        break;
      }

      case 'game/tournamentFinished': {
        if (action.payload.roomId != null) {
          unsubscribeRoom(action.payload.roomId);
          store.dispatch(roomCleared({ roomId: action.payload.roomId }));
          store.dispatch(actionLogCleared({ roomId: action.payload.roomId }));
        }
        break;
      }

      case 'game/goToGameList': {
        Object.keys(roomHandlers).forEach(roomId => unsubscribeRoom(roomId));
        store.dispatch(allRoomsCleared());
        store.dispatch(actionLogAllCleared());
        break;
      }

      case 'auth/logout': {
        // The nested action performs the same full room/private reset as an account switch.
        store.dispatch(goToGameList());
        stopSocket();
        break;
      }

      case 'game/fetchMyHand/fulfilled':
      case 'game/resyncSeat/fulfilled':
        store.dispatch(privateStateReceived(action.payload));
        break;

      case 'game/fetchGameSnapshot/fulfilled':
        if (store.getState().games.activeGames?.[action.payload?.roomId]
            && !action.payload?.removed && !action.payload?.stale
            && !isOlderRoomEvent(
              store.getState().games.activeGames?.[action.payload?.roomId],
              action.payload?.game,
            )) {
          store.dispatch(snapshotReceived(action.payload));
          // The snapshot's game payload is authoritative for wildness too, so a refresh or
          // reconnect restores the badges rather than losing them with the one-shot announce.
          if (Array.isArray(action.payload?.game?.wildCards)) {
            store.dispatch(wildCardsReceived({
              roomId: action.payload.roomId, wildCards: action.payload.game.wildCards,
            }));
          }
          // Backfill the CURRENT hand's earlier events (mid-hand open / reconnect). A snapshot is
          // fetched on open, join and every reconnect (onConnect refetches one per room), so this is
          // the single heal point. Dedup makes it idempotent against the live event stream.
          const handId = action.payload?.game?.handId;
          if (handId != null) {
            store.dispatch(fetchHandHistory({ roomId: action.payload.roomId, hand: handId }));
          }
        }
        break;

      case 'pup/playCard/fulfilled':
        store.dispatch(privateStateReceived(action.payload));
        store.dispatch(setMyCards({
          roomId: action.payload.roomId,
          cards: action.payload.holeCards ? action.payload.holeCards.split(' ') : [],
        }));
        store.dispatch(setHandInfo({
          roomId: action.payload.roomId, handInfo: action.payload.handInfo || '',
        }));
        break;

      case 'pup/queueCard/fulfilled':
      case 'pup/unqueueCard/fulfilled':
        // The HTTP snapshot can race a newer /private/pupHand push when an immediately legal
        // queued card is auto-admitted before the response reaches the browser. Follow every
        // box move with a fresh row-locked read so a late response cannot visually resurrect it.
        store.dispatch(fetchMyHand({ roomId: action.payload.roomId }));
        break;

      case 'SEND_SOCKET_MESSAGE': {
        if (client && client.connected) {
          client.publish({
            destination: action.payload.destination,
            body: JSON.stringify(action.payload.data)
          });
        }
        break;
      }

      default:
        break;
    }

    return next(action);
  };
};

export const sendSocketMessage = (destination, data) => ({
  type: 'SEND_SOCKET_MESSAGE',
  payload: { destination, data }
});

export const sendGameAction = (roomId, action, data) => ({
  type: 'SEND_SOCKET_MESSAGE',
  payload: {
    destination: `/app/game/${roomId}/${action}`,
    data
  }
});
